import React from "react";
import { Box, Menu, MenuItem, Tab, Tabs } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { enterpriseTokens } from "styles/theme";
import { CUSTOMER_DASHBOARDS } from "config";
import { useAuth } from "context/AuthContext";

const dashboardTabs = [
    { label: "Overview", path: "overview" },
    { label: "Cost", path: "cost-management" },
    { label: "Schedule", path: "schedule" },
    { label: "Engineering", path: "engineering" },
    { label: "Risk", path: "risk" },
    { label: "Procurement", path: "procurement" },
    { label: "Contracts", path: "contract-management" },
    { label: "HSE", path: "hse" },
    { label: "Lease", path: "lease" },
    { label: "Financial", path: "financial" },
];

const DashboardSubnav = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { projectId } = useParams();
    const { user } = useAuth();
    const [customerMenuAnchor, setCustomerMenuAnchor] = React.useState(null);

    const projectRoot = `/projects/${projectId}`;
    const normalizedPath = pathname.replace(/\/+$/, "");
    const isProjectOverview = normalizedPath === projectRoot;
    const isDashboardRoute = pathname.includes("/dashboard/");
    const isCustomerRole = user?.roles?.includes("Customer");
    const normalizedEmail = user?.email?.toLowerCase();
    const customerDashboards = CUSTOMER_DASHBOARDS.filter((dashboard) => {
        const emailAllowed = !dashboard.allowedEmails?.length || dashboard.allowedEmails.some((email) => email.toLowerCase() === normalizedEmail);
        const projectAllowed = !dashboard.projectIds?.length || dashboard.projectIds.includes(projectId);
        return emailAllowed && projectAllowed;
    });
    const activeCustomerDashboard = customerDashboards.find((dashboard) => pathname.includes(`/dashboard/${dashboard.path}`));

    if (!projectId || (!isProjectOverview && !isDashboardRoute)) return null;
    if (isCustomerRole && !activeCustomerDashboard) return null;

    const activeTab = isProjectOverview
        ? "overview"
        : activeCustomerDashboard
            ? "customer-dashboard"
            : dashboardTabs.find((tab) => pathname.includes(`/dashboard/${tab.path}`))?.path || false;

    const handleTabChange = (_, value) => {
        if (value === "customer-dashboard") return;
        navigate(value === "overview" ? projectRoot : `${projectRoot}/dashboard/${value}`);
    };

    const handleCustomerDashboardClick = (event) => {
        if (customerDashboards.length === 1) {
            navigate(`${projectRoot}/dashboard/${customerDashboards[0].path}`);
            return;
        }
        setCustomerMenuAnchor(event.currentTarget);
    };

    return (
        <Box sx={{ bgcolor: "#FFFFFF", borderBottom: `1px solid ${enterpriseTokens.lineSoft}`, px: { xs: 1.5, md: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 42 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons={false}
                    sx={{
                        flex: 1,
                        minHeight: 42,
                        "& .MuiTabs-indicator": { height: 2 },
                    }}
                >
                    {!isCustomerRole && dashboardTabs.map((tab) => (
                        <Tab
                            key={tab.path}
                            value={tab.path}
                            label={tab.label}
                            sx={{
                                minHeight: 42,
                                minWidth: "auto",
                                px: 1.25,
                                py: 0.5,
                                textTransform: "none",
                                fontSize: 11,
                                fontWeight: activeTab === tab.path ? 600 : 500,
                                color: activeTab === tab.path ? enterpriseTokens.primary : enterpriseTokens.muted,
                            }}
                        />
                    ))}
                    {customerDashboards.length > 0 && (
                        <Tab
                            value="customer-dashboard"
                            label={activeCustomerDashboard?.label || "Customer"}
                            onClick={handleCustomerDashboardClick}
                            sx={{ minHeight: 42, minWidth: "auto", px: 1.25, py: 0.5, textTransform: "none", fontSize: 11, fontWeight: activeCustomerDashboard ? 600 : 500, color: activeCustomerDashboard ? enterpriseTokens.primary : enterpriseTokens.muted }}
                        />
                    )}
                </Tabs>
                <Menu
                    anchorEl={customerMenuAnchor}
                    open={Boolean(customerMenuAnchor)}
                    onClose={() => setCustomerMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{ elevation: 0, sx: { mt: 0.5, minWidth: 180, border: `1px solid ${enterpriseTokens.lineSoft}`, borderRadius: "3px", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)" } }}
                >
                    {customerDashboards.map((dashboard) => (
                        <MenuItem
                            key={dashboard.key}
                            selected={activeCustomerDashboard?.key === dashboard.key}
                            onClick={() => {
                                setCustomerMenuAnchor(null);
                                navigate(`${projectRoot}/dashboard/${dashboard.path}`);
                            }}
                            sx={{ fontSize: 12 }}
                        >
                            {dashboard.label}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </Box>
    );
};

export default DashboardSubnav;
